import React, { useState, useEffect } from 'react';
import { HomeURL } from '../services/Webservice';
import {
    FaTrash, FaSearch, FaEdit, FaPlus, FaTimes,
    FaChevronLeft, FaChevronRight, FaDownload
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import './LabTests.css';

function LabTests() {
    const [tests, setTests] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingTest, setEditingTest] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    const initialFormState = {
        TestName: '', TestCode: '', DEPTID: '', MRP: '',
        SpecimenType: '', Method: '', Container: '',
        CutOff: '', Schedule: '', Reporting: '',
        Temp: '', SpecialInstructions: '', Category: '',
        RefLab: ''
    };

    const [formData, setFormData] = useState(initialFormState);

    useEffect(() => {
        fetchLabTests();
        fetchDepartments();
    }, []);

    const fetchDepartments = async () => {
        try {
            const fd = new FormData();
            fd.append('Xml', JSON.stringify([{}]));
            fd.append('Flag', '4');
            const response = await fetch(`${HomeURL}api/Admin/DeptCrud`, { method: 'POST', body: fd });
            const data = await response.json();
            if (Array.isArray(data)) setDepartments(data);
        } catch (error) { console.error('Dept Fetch Error:', error); }
    };

    const fetchLabTests = async () => {
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('Xml', JSON.stringify([{ MRP: 0 }]));
            fd.append('Flag', '4');
            const response = await fetch(`${HomeURL}api/Values/LabtestsMaster_Crud`, { method: 'POST', body: fd });
            const data = await response.json();
            setTests(Array.isArray(data) ? data : []);
        } catch (error) { console.error(error); } finally { setLoading(false); }
    };

    const handleDownloadExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(filteredTests);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "LabTests");
        XLSX.writeFile(workbook, "LabTestsData.xlsx");
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const testObject = {
            ...formData,
            TestID: editingTest ? editingTest.TestID : 0,
            DEPTID: parseInt(formData.DEPTID),
            MRP: parseFloat(formData.MRP) || 0
        };
        const fd = new FormData();
        fd.append('Xml', JSON.stringify([testObject]));
        fd.append('Flag', editingTest ? '2' : '1');
        const response = await fetch(`${HomeURL}api/Values/LabtestsMaster_Crud`, { method: 'POST', body: fd });
        if (response.ok) {
            setShowModal(false);
            fetchLabTests();
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this test?")) return;
        const fd = new FormData();
        fd.append('Xml', JSON.stringify([{ TestID: id, MRP: 0 }]));
        fd.append('Flag', '5');
        await fetch(`${HomeURL}api/Values/LabtestsMaster_Crud`, { method: 'POST', body: fd });
        fetchLabTests();
    };

    // Stylish Date UI Renderer
    const renderCreatedDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        const day = date.getDate();
        const month = date.toLocaleString('default', { month: 'short' }).toUpperCase();
        const year = date.getFullYear();

        return (
            <div className="date-ui-container">
                <span className="date-day">{day}</span>
                <div className="date-month-year">
                    <span className="date-month">{month}</span>
                    <span className="date-year">{year}</span>
                </div>
            </div>
        );
    };

    const filteredTests = tests.filter(t =>
        t.TestName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.TestCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.DEPT_NAME?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalPages = Math.ceil(filteredTests.length / itemsPerPage);
    const currentItems = filteredTests.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="lab-tests-container">
            <div className="header-flex">
                <div className="welcome-section">
                    <h1>Lab Test Master</h1>
                    <p>Search & Manage all Laboratory Tests</p>
                </div>
                <div className="header-actions">
                    <button className="download-btn-main" onClick={handleDownloadExcel}><FaDownload /> Excel</button>
                    <button className="add-btn-main" onClick={() => { setEditingTest(null); setFormData(initialFormState); setShowModal(true); }}><FaPlus /> Add Test</button>
                </div>
            </div>

            <div className="table-card">
                <div className="search-wrapper">
                    <FaSearch className="search-icon-inside" />
                    <input
                        type="text"
                        placeholder="Search by Name, Code or Department..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    />
                </div>

                <div className="table-responsive">
                    <table className="employee-style-table">
                        <thead>
                            <tr>
                                <th className="sticky-col">Actions</th>
                                <th className="test-name-cell">Test Name</th>
                                <th className="code-cell">Test Code</th>
                                <th className="dept-cell">Department</th>
                                <th>MRP (₹)</th>
                                <th>Specimen</th>
                                <th>Method</th>
                                <th>Container</th>
                                <th>Cut-Off</th>
                                <th>Schedule</th>
                                <th>Reporting</th>
                                <th>Temp</th>
                                {/*<th className="instruction-cell">Special Instructions</th>*/}
                                <th>Category</th>
                                <th>Ref Lab</th>
                                <th className="date-header">Created Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map((test) => (
                                <tr key={test.TestID}>
                                    <td className="sticky-col">
                                        <div className="action-btns-flex">
                                            <button className="edit-icon-btn" onClick={() => { setEditingTest(test); setFormData(test); setShowModal(true); }}><FaEdit /></button>
                                            <button className="delete-icon-btn" onClick={() => handleDelete(test.TestID)}><FaTrash /></button>
                                        </div>
                                    </td>
                                    <td className="test-name-cell">{test.TestName}</td>
                                    <td className="code-cell"><span className="code-badge">{test.TestCode}</span></td>
                                    <td className="dept-cell">{test.DEPT_NAME}</td>
                                    <td>₹{test.MRP}</td>
                                    <td>{test.SpecimenType}</td>
                                    <td>{test.Method}</td>
                                    <td>{test.Container}</td>
                                    <td>{test.CutOff}</td>
                                    <td>{test.Schedule}</td>
                                    <td>{test.Reporting}</td>
                                    <td>{test.Temp}</td>
                                    {/*<td className="instruction-cell">{test.SpecialInstructions || '-'}</td>*/}
                                    <td>{test.Category}</td>
                                    <td>{test.RefLab}</td>
                                    <td className="date-cell-content">{renderCreatedDate(test.CreatedDate)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Fix */}
                <div className="pagination-footer">
                    <div className="pagination-info">Showing <b>{currentItems.length}</b> of <b>{filteredTests.length}</b> tests</div>
                    <div className="pagination-controls">
                        <button className="pagi-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)}>
                            <FaChevronLeft /> Previous
                        </button>
                        <span className="page-indicator">Page {currentPage} of {totalPages || 1}</span>
                        <button className="pagi-btn" disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(prev => prev + 1)}>
                            Next <FaChevronRight />
                        </button>
                    </div>
                </div>
            </div>

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-content large-modal">
                        <div className="modal-header">
                            <h3>{editingTest ? 'Edit Test' : 'Add Test'}</h3>
                            <FaTimes className="close-x" onClick={() => setShowModal(false)} />
                        </div>
                        <form onSubmit={handleSubmit} className="modal-form">
                            {/* Scrollable Body */}
                            <div className="modal-body-scrollable">
                                <div className="modal-body-grid">
                                    <div className="input-box"><label>Test Name</label><input name="TestName" required value={formData.TestName} onChange={handleInputChange} /></div>
                                    <div className="input-box"><label>Test Code</label><input name="TestCode" required value={formData.TestCode} onChange={handleInputChange} /></div>
                                    <div className="input-box">
                                        <label>Department</label>
                                        <select name="DEPTID" required value={formData.DEPTID} onChange={handleInputChange}>
                                            <option value="">Select Dept</option>
                                            {departments.map(d => <option key={d.DEPT_ID} value={d.DEPT_ID}>{d.DEPT_NAME}</option>)}
                                        </select>
                                    </div>
                                    <div className="input-box"><label>MRP (₹)</label><input name="MRP" type="number" value={formData.MRP} onChange={handleInputChange} /></div>
                                    <div className="input-box"><label>Specimen Type</label><input name="SpecimenType" value={formData.SpecimenType} onChange={handleInputChange} /></div>
                                    <div className="input-box"><label>Method</label><input name="Method" value={formData.Method} onChange={handleInputChange} /></div>
                                    <div className="input-box"><label>Container</label><input name="Container" value={formData.Container} onChange={handleInputChange} /></div>
                                    <div className="input-box"><label>Cut-Off Time</label><input name="CutOff" value={formData.CutOff} onChange={handleInputChange} /></div>
                                    <div className="input-box"><label>Schedule</label><input name="Schedule" value={formData.Schedule} onChange={handleInputChange} /></div>
                                    <div className="input-box"><label>Reporting</label><input name="Reporting" value={formData.Reporting} onChange={handleInputChange} /></div>
                                    <div className="input-box"><label>Temperature</label><input name="Temp" value={formData.Temp} onChange={handleInputChange} /></div>
                                    <div className="input-box"><label>Category</label><input name="Category" value={formData.Category} onChange={handleInputChange} /></div>
                                    <div className="input-box full-width"><label>Reference Lab</label><input name="RefLab" value={formData.RefLab} onChange={handleInputChange} /></div>
                                    <div className="input-box full-width">
                                        <label>Special Instructions</label>
                                        <textarea name="SpecialInstructions" rows="4" value={formData.SpecialInstructions} onChange={handleInputChange} />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="submit" className="save-btn">{editingTest ? 'Update Test' : 'Submit Test'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LabTests;    